import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import tradingService from '../services/tradingService';
import { isCanceledRequest } from '../services/api';
import './AccountsPage.css';
import AccountCard from '../components/AccountCard';
import { getPerformanceSummary } from '../utils/accountPerformance';

function CreateAccountModal({ open, onClose, exchanges, onCreated }) {
  const [label, setLabel] = useState('New account');
  const [exchangeId, setExchangeId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setError('');
    setSubmitting(false);
    setLabel('New account');
    setExchangeId(exchanges?.[0]?.exchange_id ? String(exchanges[0].exchange_id) : '');
  }, [open, exchanges]);

  if (!open) return null;

  const canSubmit = !!exchangeId && !submitting;
  const selectedExchange = exchanges.find((exchange) => String(exchange.exchange_id) === exchangeId);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const res = await tradingService.accounts.create({
        label,
        exchangeId: Number(exchangeId),
      });

      const createdNumber = res?.data?.number;
      if (!createdNumber) throw new Error('Create succeeded but response has no account number');

      onCreated(createdNumber);
    } catch (err) {
      setError(err?.message || 'Failed to create account');
      setSubmitting(false);
    }
  };

  return (
    <div onClick={onClose} className="accounts-modal__overlay">
      <div onClick={(e) => e.stopPropagation()} className="card accounts-modal__dialog">
        <div className="accounts-modal__header">
          <h2 className="accounts-modal__title">Create account</h2>
          <button type="button" onClick={onClose}>
            Close
          </button>
        </div>

        <form onSubmit={submit} className="accounts-modal__form">
          <div className="accounts-modal__grid">
            <label>
              <div className="accounts-modal__field-title">Label</div>
              <input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. Main Binance"
                className="accounts-modal__control"
              />
            </label>

            <label>
              <div className="accounts-modal__field-title">Exchange</div>
              <select
                value={exchangeId}
                onChange={(e) => setExchangeId(e.target.value)}
                className="accounts-modal__control"
              >
                <option value="" disabled>
                  Select exchange…
                </option>
                {exchanges.map((ex) => (
                  <option key={ex.id} value={String(ex.exchange_id)}>
                    {ex.exchange_name ?? `Exchange #${ex.exchange_id}`}
                  </option>
                ))}
              </select>
            </label>

            {selectedExchange?.affiliate_link ? (
              <a
                href={selectedExchange.affiliate_link}
                target="_blank"
                rel="noreferrer"
              >
                Sign up for {selectedExchange.exchange_name} through our partner link
              </a>
            ) : null}

            {error ? <div className="accounts-modal__error">{error}</div> : null}

            <div className="accounts-modal__actions">
              <button type="button" onClick={onClose} disabled={submitting}>
                Cancel
              </button>
              <button type="submit" disabled={!canSubmit}>
                {submitting ? 'Creating…' : 'Create'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AccountsPage() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [loadingExchanges, setLoadingExchanges] = useState(true);
  const [error, setError] = useState('');

  const [accounts, setAccounts] = useState([]);
  const [exchanges, setExchanges] = useState([]);
  const [valuationsByNumber, setValuationsByNumber] = useState({});

  const [createOpen, setCreateOpen] = useState(false);

  const [quickFilter, setQuickFilter] = useState('all'); // 'all' | 'strategies' | 'subscriptions'

  const exchangesById = useMemo(() => {
    const m = new Map();
    for (const ex of exchanges) m.set(String(ex.id), ex);
    return m;
  }, [exchanges]);

  useEffect(() => {
    const ac = new AbortController();

    (async () => {
      try {
        setLoading(true);
        setError('');

        const res = await tradingService.accounts.list({
          limit: 10,
          offset: 0,
          strategies_only: quickFilter === 'strategies' ? 'true' : undefined,
          subscriptions_only: quickFilter === 'subscriptions' ? 'true' : undefined,
          signal: ac.signal,
        });

        setAccounts(res?.data?.accounts ?? []);
      } catch (err) {
        if (isCanceledRequest(err)) {
          return;
        }

        setError(err?.message || 'Failed to load accounts');
      } finally {
        if (!ac.signal.aborted) {
          setLoading(false);
        }
      }
    })();

    return () => ac.abort();
  }, [quickFilter]);

  useEffect(() => {
    if (!accounts.length) {
      setValuationsByNumber({});
      return undefined;
    }

    const ac = new AbortController();

    setValuationsByNumber({});

    (async () => {
      const results = await Promise.allSettled(
        accounts.map(async (account) => {
          const res = await tradingService.accounts.performance.history({
            number: account.number,
            limit: 5000,
            signal: ac.signal,
          });

          const history = res?.data?.history ?? [];
          return [account.number, getPerformanceSummary(history)];
        })
      );

      if (ac.signal.aborted) {
        return;
      }

      const next = {};
      for (const result of results) {
        if (result.status === 'fulfilled') {
          const [number, summary] = result.value;
          next[number] = summary;
        }
      }

      setValuationsByNumber(next);
    })();

    return () => ac.abort();
  }, [accounts]);

  useEffect(() => {
    const ac = new AbortController();

    (async () => {
      try {
        setLoadingExchanges(true);
        const res = await tradingService.dicts.exchanges({ signal: ac.signal });
        const list = res?.data?.exchanges ?? res?.data ?? [];
        setExchanges(Array.isArray(list) ? list : []);
      } catch (err) {
        if (isCanceledRequest(err)) {
          return;
        }

        setExchanges([]);
      } finally {
        if (!ac.signal.aborted) {
          setLoadingExchanges(false);
        }
      }
    })();

    return () => ac.abort();
  }, []);

  const onCreated = (number) => {
    setCreateOpen(false);
    navigate(`/my/accounts/${encodeURIComponent(number)}`);
  };

  return (
    <div className="card">
      <div className="accounts-page__header">
        <h1 className="accounts-page__title">Accounts</h1>

        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          disabled={loadingExchanges || exchanges.length === 0}
          title={exchanges.length === 0 ? 'No exchanges available' : 'Create new account'}
        >
          + Create account
        </button>
      </div>

      <div className="accounts-page__quick-filters" role="tablist" aria-label="Account quick filters">
        <button
          type="button"
          className={quickFilter === 'all' ? 'accounts-page__qf-btn accounts-page__qf-btn--active' : 'accounts-page__qf-btn'}
          onClick={() => setQuickFilter('all')}
          role="tab"
          aria-selected={quickFilter === 'all'}
          disabled={loading}
        >
          All accounts
        </button>
        <button
          type="button"
          className={
            quickFilter === 'strategies' ? 'accounts-page__qf-btn accounts-page__qf-btn--active' : 'accounts-page__qf-btn'
          }
          onClick={() => setQuickFilter('strategies')}
          role="tab"
          aria-selected={quickFilter === 'strategies'}
          disabled={loading}
        >
          Accounts with active strategies
        </button>
        <button
          type="button"
          className={
            quickFilter === 'subscriptions'
              ? 'accounts-page__qf-btn accounts-page__qf-btn--active'
              : 'accounts-page__qf-btn'
          }
          onClick={() => setQuickFilter('subscriptions')}
          role="tab"
          aria-selected={quickFilter === 'subscriptions'}
          disabled={loading}
        >
          Accounts with active subscriptions
        </button>
      </div>

      {error ? <p className="accounts-page__error">{error}</p> : null}
      {loading ? <p>Loading…</p> : null}

      {!loading ? (
        <div className="accounts-page__cards">
          {accounts.length === 0 ? (
            <div className="accounts-page__empty-card">
              <div className="accounts-page__empty-title">
                {quickFilter === 'all' ? 'No accounts yet' : 'No accounts match this filter'}
              </div>
              <div className="accounts-page__empty-subtitle">
                {quickFilter === 'all'
                  ? 'Create your first account to start connecting exchanges and trading.'
                  : 'Try a different quick filter.'}
              </div>
            </div>
          ) : (
            accounts.map((acc) => {
              const exchangeName =
                acc?.exchange?.exchange_name ??
                exchangesById.get(String(acc?.exchange?.exchange_id))?.exchange_name ??
                '—';

              const openAccount = () => navigate(`/my/accounts/${encodeURIComponent(acc.number)}`);

              return (
                <AccountCard
                  key={acc.number}
                  account={acc}
                  exchangeName={exchangeName}
                  valuation={valuationsByNumber[acc.number]}
                  onOpen={openAccount}
                />
              );
            })
          )}
        </div>
      ) : null}

      <CreateAccountModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        exchanges={exchanges}
        onCreated={onCreated}
      />
    </div>
  );
}
